import React, { createContext, forwardRef, useContext, useEffect, useId, useState } from 'react';
import { Box, Checkbox, FormControlLabel, FormHelperText, Radio, Switch, TextField, Typography } from '@mui/material';

const GroupContext = createContext({});
const Form = forwardRef(({ as = 'form', validated, ...props }, ref) => <Box component={as} ref={ref} {...props} />);
Form.displayName = 'Form';

Form.Group = function FormGroup({ controlId, as = 'div', children, className = '', ...props }) {
  const generatedId = useId();
  const [invalid, setInvalid] = useState(false);
  return <GroupContext.Provider value={{ id: controlId || generatedId, invalid, setInvalid }}>
    <Box component={as} className={'sugi-form-group ' + className} {...props}>{children}</Box>
  </GroupContext.Provider>;
};
Form.Label = function FormLabel({ htmlFor, children, className = '', column, ...props }) {
  const group = useContext(GroupContext);
  return <Typography component="label" htmlFor={htmlFor || group.id} className={'form-label sugi-form-label ' + className} {...props}>{children}</Typography>;
};

const Control = forwardRef(({ as, type = 'text', id, isInvalid = false, isValid, plaintext, size, className = '', style, value, defaultValue, disabled, required, onChange, onBlur, onFocus, onKeyDown, autoFocus, rows, children, ...inputProps }, ref) => {
  const group = useContext(GroupContext);
  const setInvalid = group.setInvalid;
  useEffect(() => { setInvalid?.(isInvalid); }, [setInvalid, isInvalid]);
  const cleanClasses = className.replace(/\bform-control(?:-sm|-lg)?\b/g, '');
  if (type === 'file' || type === 'color') {
    return <input ref={ref} type={type} id={id || group.id} className={'form-control ' + (isInvalid ? 'is-invalid ' : '') + cleanClasses}
      style={style} value={value} defaultValue={defaultValue} disabled={disabled} required={required} onChange={onChange} onBlur={onBlur} onFocus={onFocus} onKeyDown={onKeyDown} autoFocus={autoFocus} {...inputProps} />;
  }
  return <TextField fullWidth variant="outlined" size={size === 'lg' ? 'medium' : 'small'} id={id || group.id}
    className={'sugi-field ' + cleanClasses} style={style} type={type} value={value} defaultValue={defaultValue}
    disabled={disabled} required={required} error={isInvalid} onChange={onChange} onBlur={onBlur} onFocus={onFocus} onKeyDown={onKeyDown} autoFocus={autoFocus} inputRef={ref}
    multiline={as === 'textarea'} rows={rows} InputProps={{ readOnly: plaintext || inputProps.readOnly }} inputProps={{ ...inputProps, 'aria-describedby': inputProps['aria-describedby'] || (isInvalid && group.id ? group.id + '-feedback' : undefined) }} />;
});
Control.displayName = 'Form.Control';
Control.Feedback = function Feedback({ children, type, ...props }) {
  const { invalid, id } = useContext(GroupContext);
  return invalid ? <FormHelperText id={id ? id + '-feedback' : undefined} error={type === 'invalid'} role="alert" {...props}>{children}</FormHelperText> : null;
};
Form.Control = Control;
Form.Select = forwardRef(({ id, isInvalid = false, size, className = '', style, value, defaultValue, disabled, required, onChange, children, ...inputProps }, ref) => {
  const group = useContext(GroupContext);
  const setInvalid = group.setInvalid;
  useEffect(() => { setInvalid?.(isInvalid); }, [setInvalid, isInvalid]);
  return <TextField select SelectProps={{ native: true }} fullWidth variant="outlined" size={size === 'lg' ? 'medium' : 'small'}
    id={id || group.id} className={'sugi-field ' + className.replace(/\bform-select(?:-sm|-lg)?\b/g, '')} style={style}
    value={value} defaultValue={defaultValue} disabled={disabled} required={required} error={isInvalid} onChange={onChange}
    inputRef={ref} inputProps={inputProps}>{children}</TextField>;
});
Form.Select.displayName = 'Form.Select';
Form.Check = function FormCheck({ type, label, inline, className = '', id, ...props }) {
  const generatedId = useId();
  const Component = type === 'switch' ? Switch : type === 'radio' ? Radio : Checkbox;
  const control = <Component size="small" id={id || generatedId} {...props} />;
  return label ? <FormControlLabel className={className} control={control} label={label} sx={{ display: inline ? 'inline-flex' : 'flex', '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }} /> : control;
};
Form.Text = ({ children, ...props }) => <Typography component="small" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }} {...props}>{children}</Typography>;
export default Form;
